import React, { useState, useEffect } from "react";
import { Trans, useTranslation } from "react-i18next";

import BreadCrumb from "../../components/Navbars/BreadCrumb";
import MainHeader from "../../components/Headers/MainHeader";
import {
    NOTIFICATION_TYPES,
    PARAMETER_CONSTRAINTS_OPERATIONS,
} from "../../utils/constants";
import MainLoadingButton from "../../components/Buttons/MainLoadingButton";
import { Link } from "react-router-dom";
import AlertsAPI from "../../API/alerts.api";
import alertsSchema from "../../schemas/alerts.schema";
import { validate } from "../../utils/ValidationHelpers";
import { ErrorAlert, SuccessAlert } from "../../utils/Alerts";
import { useHistory, useLocation } from "react-router-dom";
import AlertConfiguration from "./AlertConfiguration";
import NotificationConfiguration from "./NotificationConfiguration";
import { isEmpty } from "lodash";

const AlertsForm = () => {
    //General states
    const { t } = useTranslation();
    const history = useHistory();
    const location = useLocation();
    const editAlertId = location.state?.alertId;
    const [isFormSubmitting, setIsFormSubmitting] = useState(false);

    //Data states
    const [data, setData] = useState({});
    const [parameterConstraints, setParameterConstraints] = useState([{}]);
    const [alertRecipients, setAlertRecipients] = useState([]);

    //Validation states
    const [isFormValid, setIsFormValid] = useState(false);
    const [isFormSubmitted, setIsFormSubmitted] = useState(false);
    const [error, setError] = useState({});

    const breadCrumbs = [
        {
            path: "/dashboard",
            element: () => <i className="fas fa-home" />,
        },
        {
            path: "/options/alerts",
            element: () => <Trans i18nKey="alerts">Alerts</Trans>,
        },
        {
            element: () => t("newAlert"),
        },
    ];

    //Use effects
    useEffect(() => {
        if (editAlertId) {
            fetchAlert(editAlertId).then((response) => {
                handleEditDataMapping(response);
            });
        }
    }, []);

    //From validations
    useEffect(() => {
        validate(
            {
                ...data,
                parameterConstraints: parameterConstraints,
                recipients: alertRecipients,
                notificationTypes: minimumNotificationTypeSet(),
            },
            alertsSchema.alertFormSchema,
            setIsFormValid,
            setError
        );
    }, [data, parameterConstraints, alertRecipients]);

    //Data fetching
    const fetchAlert = async (id) => {
        const result = await AlertsAPI.getAlertById(id);
        return result;
    };

    //Handlers
    const handleEditDataMapping = (data) => {
        setData({
            name: data.name,
            material: data.material,
            devices: data.scanners,
            lots: data.lots,
            suppliers: data.suppliers,
            notificationConfigurations: {
                emailNotification: data.notificationConfigurations.find(
                    (elem) => elem.notificationType === NOTIFICATION_TYPES.email
                ),
                systemNotification: data.notificationConfigurations.find(
                    (elem) =>
                        elem.notificationType === NOTIFICATION_TYPES.system
                ),
            },
        });
        const parameterConst = [];
        data.parameters.forEach((elem) => {
            const paramIndex = parameterConst.findIndex(
                (param) => param.parameter?.value === elem.id
            );
            if (paramIndex !== -1) {
                parameterConst[paramIndex] = {
                    ...parameterConst[paramIndex],
                    ...(elem.opertionType ===
                        PARAMETER_CONSTRAINTS_OPERATIONS.min && {
                        min: elem.value,
                    }),
                    ...(elem.opertionType ===
                        PARAMETER_CONSTRAINTS_OPERATIONS.max && {
                        max: elem.value,
                    }),
                    ...(elem.opertionType ===
                        PARAMETER_CONSTRAINTS_OPERATIONS.eq && {
                        equals: elem.value,
                    }),
                };
            } else {
                parameterConst.push({
                    parameter: {
                        label: elem?.name,
                        value: elem?.id,
                    },
                    ...(elem.opertionType ===
                        PARAMETER_CONSTRAINTS_OPERATIONS.min && {
                        min: elem.value,
                    }),
                    ...(elem.opertionType ===
                        PARAMETER_CONSTRAINTS_OPERATIONS.max && {
                        max: elem.value,
                    }),
                    ...(elem.opertionType ===
                        PARAMETER_CONSTRAINTS_OPERATIONS.eq && {
                        equals: elem.value,
                    }),
                });
            }
        });
        setParameterConstraints(parameterConst);
        setAlertRecipients(data.notificationConfigurations[0]?.recipients);
    };

    const handleSubmitAlert = async () => {
        setIsFormSubmitted(true);

        if (!isFormValid) return;
        setIsFormSubmitting(true);
        let parameters = [];
        parameterConstraints.forEach((elem) => {
            if (elem.min !== undefined) {
                parameters.push({
                    id: elem.parameter.value,
                    name: elem.parameter.label,
                    opertionType: PARAMETER_CONSTRAINTS_OPERATIONS.min,
                    value: +elem.min,
                });
            }
            if (elem.max !== undefined) {
                parameters.push({
                    id: elem.parameter.value,
                    name: elem.parameter.label,
                    opertionType: PARAMETER_CONSTRAINTS_OPERATIONS.max,
                    value: +elem.max,
                });
            }
            if (elem.equals !== undefined) {
                parameters.push({
                    id: elem.parameter.value,
                    name: elem.parameter.label,
                    opertionType: PARAMETER_CONSTRAINTS_OPERATIONS.eq,
                    value: +elem.equals,
                });
            }
        });

        const notificationConfigurations = [];

        if (data.notificationConfigurations.emailNotification)
            notificationConfigurations.push({
                notificationType: NOTIFICATION_TYPES.email,
                recipients: alertRecipients,
            });
        if (data.notificationConfigurations.systemNotification)
            notificationConfigurations.push({
                notificationType: NOTIFICATION_TYPES.system,
                recipients: alertRecipients,
            });

        const payload = {
            material: data.material,
            lots: data?.lots,
            scanners: data?.devices,
            suppliers: data?.suppliers,
            name: data.name,
            parameters,
            notificationConfigurations,
        };

        if (editAlertId) submitUpdateAlert(payload);
        else submitCreateAlert(payload);

        setIsFormSubmitting(false);
    };

    const submitCreateAlert = async (payload) => {
        const result = await AlertsAPI.createAlert(payload);
        if (result.inserted) {
            SuccessAlert(t("alertCreationSuccess"));
            history.push("/options/alerts");
        } else if (!isEmpty(result.data)) {
            const errorMessages = {};
            result.data.forEach((elem) => {
                errorMessages[`parameter.${elem}.exist`] =
                    "Parameter already exists in another alert";
            });
            setError(errorMessages);
            ErrorAlert(result.errors);
        } else {
            ErrorAlert(t("requestError"));
            history.push("/options/alerts");
        }
    };

    const submitUpdateAlert = async (payload) => {
        const result = await AlertsAPI.updateAlert({
            ...payload,
            id: editAlertId,
        });
        if (result.modified) {
            SuccessAlert(t("alertEditSuccess"));
            history.push("/options/alerts");
        } else if (!isEmpty(result.data)) {
            const errorMessages = {};
            result.data.forEach((elem) => {
                errorMessages[`parameter.${elem}.exist`] =
                    "Parameter already exists in another alert";
            });
            setError(errorMessages);
            ErrorAlert(result.errors);
        } else {
            ErrorAlert(t("requestError"));
            history.push("/options/alerts");
        }
    };

    const minimumNotificationTypeSet = () => {
        return Boolean(
            data.notificationConfigurations?.emailNotification ||
                data.notificationConfigurations?.systemNotification
        );
    };

    return (
        <>
            <BreadCrumb crumbs={breadCrumbs} />
            <MainHeader
                header={editAlertId ? t("editAlert") : t("newAlert")}
                icon={<i className={"fas fa-exclamation-triangle"}></i>}
            />
            <div className="card-table shadow flex flex-col w-full">
                <AlertConfiguration
                    data={data}
                    setData={setData}
                    parameterConstraints={parameterConstraints}
                    setParameterConstraints={setParameterConstraints}
                    isFormSubmitted={isFormSubmitted}
                    error={error}
                />
                <NotificationConfiguration
                    data={data}
                    setData={setData}
                    alertRecipients={alertRecipients}
                    setAlertRecipients={setAlertRecipients}
                    isFormSubmitted={isFormSubmitted}
                    error={error}
                    setError={setError}
                />
                <div className="flex justify-end">
                    <Link to="/options/alerts">
                        <button className="secondary-blue-btn mr-2">
                            <Trans i18nKey="cancel">Cancel</Trans>
                        </button>
                    </Link>

                    <MainLoadingButton
                        buttonText={editAlertId ? t("edit") : t("submit")}
                        isRequesting={isFormSubmitting}
                        onClick={handleSubmitAlert}
                        isDisabled={!isFormValid && isFormSubmitted}
                    />
                </div>
            </div>
        </>
    );
};

export default AlertsForm;
